"use client";

import type { PublicBoardEntry } from "@repo/api/use-cases/assessments/public-board";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui";
import { useState } from "react";

import { durationLabel, initial } from "@/lib/assessments/board-format";
import { cn, truncateText } from "@/lib/utils";

/*
 * Geometry, in viewBox units. Fixed for the same reason the table below carries
 * a `min-w`: squashing an axis onto a phone makes every tick unreadable, so the
 * figure scrolls sideways inside itself instead.
 */
const W = 880;
const H = 494;
const PAD_L = 74;
const PAD_R = 30;
const PAD_T = 58;
const PAD_B = 66;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;
const TICKS = [0, 0.25, 0.5, 0.75, 1];

/*
 * Mark sizes, in screen pixels rather than viewBox units.
 *
 * A face has to be big enough to be a face and small enough that fifty of them
 * read as a cloud rather than a collage, which puts these at 18px. Fixed in
 * pixels because that judgement is about a face, not about a share of the plot:
 * a mark that scaled with the container would be a thumbnail on a laptop and a
 * portrait on a monitor. The named few are drawn larger because a label
 * pointing at an 18px disc has to point somewhere.
 */
const MARK_PX = 18;
const NAMED_PX = 30;
/** How many of the board's leaders get a name printed beside them. */
const NAMED_COUNT = 3;

/** One attempt with both measurements, which is the only kind this can plot. */
interface Point {
  attemptId: string;
  name: string | null;
  image: string | null;
  minutes: number;
  rarity: number;
  confirmed: number | null;
  submitted: number;
}

function label(point: Point): string {
  return point.name ?? "Anonymous";
}

/**
 * A ceiling a tick can be printed against.
 *
 * Rounded up rather than taken raw, so the rightmost dot is not pinned to the
 * axis end and the labels are numbers somebody would say out loud.
 */
function ceiling(values: number[], step: number): number {
  const max = Math.max(...values);
  return Math.max(step, Math.ceil(max / step) * step);
}

/**
 * The middle value, which is where the two dashed lines are drawn.
 *
 * A median rather than a mean because both axes can carry a long tail: one
 * three-hour sitting, or one unusually rare set of findings, should not move
 * the line everybody else is read against.
 */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
    : (sorted[mid] ?? 0);
}

/**
 * A tick's text, taken from the same number that positions it.
 *
 * The pair has to come from one value or the axis mislabels its own lines: at
 * yMax 30 the old quarter ticks were drawn at 7.5 and 22.5 and printed 8 and
 * 23, so a face read against them gave the wrong rarity. Integers print bare,
 * and the median -- the only tick that is not one -- keeps a place.
 */
function tickLabel(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * A count as the sentence has to say it, with the agreement that follows.
 *
 * The quantifier and the noun were chosen independently once, and on this
 * board's own state -- where every row is untimed -- that printed "every
 * attempts here were": "every" takes a singular noun and a singular verb.
 * Returned together so a caller cannot get one right and the other wrong.
 */
function counted(
  count: number,
  total: number,
): { subject: string; singular: boolean } {
  if (count === 1) {
    return { subject: "1", singular: true };
  }
  return count === total
    ? { subject: "every", singular: true }
    : { subject: String(count), singular: false };
}

/*
 * Where the names go: one row above the plot, each over its own mark.
 *
 * Two earlier tries were both ambiguous. Offsetting each name from its own mark
 * put all three within a few units of each other, because the named few are the
 * board's leaders and therefore all near the top of the plot; stacking them in
 * rows near the top-left left each one sitting beside somebody else's face, so a
 * reader could not tell which name belonged to which mark.
 *
 * Above the plot, nothing can cover a name and no name can cover a mark. Each
 * starts directly over the mark it names, which is what keeps the leaders short,
 * and is pushed right only as far as it takes to clear the name before it. So
 * three leaders spread across the plot stay nearly vertical, and three bunched
 * in one corner fan out just enough to be told apart. Order is preserved, so a
 * leader never crosses another.
 */
const LABEL_Y = 30;
/** Enough for the longest name a label will print, which is about 150 units. */
export const LABEL_GAP = 172;
/** How close to either edge a name may sit before it would be clipped. */
export const LABEL_INSET = 74;

export function labelPositions(marks: number[]): number[] {
  const placed: number[] = [];
  let previous = -Infinity;
  for (const at of marks) {
    const next = Math.max(at, previous + LABEL_GAP, PAD_L + LABEL_INSET);
    placed.push(next);
    previous = next;
  }
  // Pushing right can run the last one off the plot; slide the row back if so.
  const overflow = (placed.at(-1) ?? 0) - (W - PAD_R - LABEL_INSET);
  return overflow > 0 ? placed.map((at) => at - overflow) : placed;
}

/**
 * How long a sitting took against what it found.
 *
 * Two measurements per person, so a scatter rather than anything ordered: the
 * question is whether the two are related at all, and a reader answers that from
 * the shape of the cloud. Nothing here is a ranking, and the leaderboard above
 * it already carries the order.
 *
 * Rarity for the vertical, because it is what the board's order is made on. Both
 * axes start at zero: rarity's zero is real -- it is what somebody scores when
 * every finding they had was also found by somebody else -- and a time axis that
 * started anywhere else would exaggerate small differences in a field that may
 * turn out to be tightly clustered.
 *
 * Each mark is the participant's own picture, the same one the table above uses.
 * The board is a named, public thing, and a reader who spots a mark in the top
 * corner wants to know whose it is without hunting for it in the table. Nothing
 * is shown here that the table does not already show: a row published without a
 * name gets an empty dashed disc, exactly as it gets an empty dashed avatar in
 * the table.
 *
 * The axes are drawn in SVG and the marks are laid over them in HTML, which is
 * not fussiness. An avatar URL is hosted by whoever the participant signed in
 * with and a good number of them are already dead -- LinkedIn's carry an expiry
 * -- and an SVG <image> that fails paints the browser's broken-image glyph with
 * no way back: its error fires while the server-rendered markup is still being
 * hydrated, so an onError handler is attached too late to ever see it. Radix's
 * Avatar loads the picture itself and shows the initial when it cannot, which is
 * exactly what the table beside this already does with the same URLs. Both
 * layers are positioned as a percentage of the same box, so they stay registered
 * at every width.
 *
 * The two dashed lines are the medians of the axes they cross, and that is all
 * they claim. There is deliberately no shaded "good" corner: the exam has no
 * clock, so a region drawn around short sittings would assert the opposite of
 * what the caption says.
 */
export function TimeQualityScatter({
  entries,
}: {
  entries: readonly PublicBoardEntry[];
}) {
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(
    null,
  );

  const points: Point[] = entries.flatMap((entry) =>
    entry.minutes !== null && entry.rarity !== null
      ? [
          {
            attemptId: entry.attemptId,
            name: entry.name,
            image: entry.image,
            minutes: entry.minutes,
            rarity: entry.rarity,
            confirmed: entry.confirmed,
            submitted: entry.submitted,
          },
        ]
      : [],
  );

  /*
   * Every reason a row cannot be plotted, counted separately, because they are
   * three different facts and the copy says which one applies.
   *
   * A sitting with no duration is the one that will dominate for a while:
   * nothing opened an attempt before submission until recently, so every
   * attempt sat before that measured the length of its own insert and is
   * untimed for good.
   *
   * The other two both look like a null rarity and are not the same thing.
   * `scored` is what separates them, which is what it is on `PublicBoardEntry`
   * for: an attempt still with the checkers has no result at all, while a
   * scored one with no rarity has a result whose findings have not been
   * partitioned yet -- the state the panel above this calls "not grouped".
   * These three and `points` partition the board.
   */
  const untimed = entries.filter((entry) => entry.minutes === null).length;
  const unscored = entries.filter(
    (entry) => entry.minutes !== null && !entry.scored,
  ).length;
  const ungrouped = entries.filter(
    (entry) => entry.minutes !== null && entry.scored && entry.rarity === null,
  ).length;
  const timing = counted(untimed, entries.length);

  if (points.length === 0) {
    /*
     * Three reasons a dot cannot be drawn, and the copy has to say which ones
     * apply. Claiming nothing was timed, on a board where every sitting was
     * timed, or that nothing was scored where the results are in and only the
     * grouping is missing, points the reader at the wrong thing to wait for --
     * and in the second case contradicts the panel directly above it.
     *
     * The headline follows the panel's own split, because on a board with no
     * untimed rows the reader has just read that sentence.
     */
    return (
      <div className="border-marketing-border-light bg-marketing-surface rounded-[14px] border border-dashed px-6 py-12 text-center">
        <p className="assess-h2 text-marketing-text">
          {untimed > 0
            ? "No sitting on this board can be plotted yet."
            : ungrouped > 0
              ? "Nothing on this board has been grouped yet."
              : "Nothing on this board has been scored yet."}
        </p>
        <p className="assess-sub text-marketing-text-muted mx-auto mt-2.5 max-w-[62ch]">
          {untimed > 0 ? (
            <>
              A sitting is measured from the moment somebody opens the exam
              room, and {timing.subject}{" "}
              {timing.singular ? "attempt here was" : "attempts here were"}{" "}
              submitted before that was recorded, so{" "}
              {timing.singular ? "it carries" : "they carry"} no duration and
              never will.{" "}
            </>
          ) : null}
          {unscored > 0 ? (
            <>
              {String(unscored)}{" "}
              {unscored === 1 ? "has been timed" : "have been timed"} and{" "}
              {unscored === 1 ? "is" : "are"} still with the checkers, so{" "}
              {unscored === 1 ? "it has" : "they have"} no result yet.{" "}
            </>
          ) : null}
          {ungrouped > 0 ? (
            <>
              {String(ungrouped)} {ungrouped === 1 ? "has been" : "have been"}{" "}
              timed and scored, but{" "}
              {ungrouped === 1 ? "its findings have" : "their findings have"}{" "}
              not been grouped yet, so{" "}
              {ungrouped === 1 ? "it carries" : "they carry"} no rarity, which
              is what the vertical axis plots.{" "}
            </>
          ) : null}
          {untimed > 0 && unscored === 0 && ungrouped === 0
            ? "This fills in as the exam is sat again."
            : unscored === 0
              ? "This fills in once the findings are grouped."
              : "This fills in as the findings are checked and grouped."}
        </p>
      </div>
    );
  }

  const xMax = ceiling(
    points.map((point) => point.minutes),
    15,
  );
  const yMax = ceiling(
    points.map((point) => point.rarity),
    5,
  );
  const x = (minutes: number) => PAD_L + (minutes / xMax) * INNER_W;
  const y = (rarity: number) => PAD_T + INNER_H - (rarity / yMax) * INNER_H;
  const axisFloor = PAD_T + INNER_H;

  const midTime = median(points.map((point) => point.minutes));
  const midRarity = median(points.map((point) => point.rarity));

  /*
   * The leaders get their names printed, because rarity is what the board is
   * ordered on and "who is in the top corner" is the first thing a reader looks
   * for. Drawn after everybody else, so a name is never under a face.
   */
  const named = [...points]
    .sort((a, b) => b.rarity - a.rarity)
    .slice(0, NAMED_COUNT);
  const isNamed = new Set(named.map((point) => point.attemptId));
  const byTime = [...named].sort((a, b) => a.minutes - b.minutes);
  const labelX = new Map(
    labelPositions(byTime.map((point) => x(point.minutes))).map(
      (at, index) => [byTime[index]?.attemptId ?? "", at] as const,
    ),
  );

  /*
   * Both layers read the same box, so a mark and the axis under it agree at any
   * width: the SVG fills the container and keeps its aspect, which makes a
   * percentage of the container the same thing as a percentage of the viewBox.
   */
  const leftPct = (minutes: number) => (x(minutes) / W) * 100;
  const topPct = (rarity: number) => (y(rarity) / H) * 100;

  /** One mark. A row published without a name gets a disc, never a stand-in face. */
  const mark = (point: Point, leader: boolean) => {
    const size = leader ? NAMED_PX : MARK_PX;
    if (point.name === null) {
      return (
        <span
          className="border-marketing-text-secondary bg-marketing-cream block rounded-full border border-dashed"
          style={{ height: size, width: size }}
        />
      );
    }
    return (
      <Avatar
        className={cn(
          // The light ring is the gap that keeps two people who sat for the
          // same length of time from reading as one face.
          "ring-marketing-cream-light ring-2",
          leader && "outline-marketing-text outline-2 outline-offset-2",
        )}
        style={{ height: size, width: size }}
      >
        <AvatarImage alt="" src={point.image ?? undefined} />
        <AvatarFallback
          className="bg-marketing-cream text-marketing-text-muted font-medium"
          style={{ fontSize: leader ? 13 : 9 }}
        >
          {initial(point.name)}
        </AvatarFallback>
      </Avatar>
    );
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <div
          aria-label={`Scatter plot of rarity against sitting length for ${String(points.length)} ${points.length === 1 ? "attempt" : "attempts"} on this board, each drawn as the participant's picture.`}
          className="relative min-w-[820px]"
          role="img"
        >
          <svg
            aria-hidden
            className="block w-full"
            viewBox={`0 0 ${String(W)} ${String(H)}`}
          >
            {/*
              The frame, as one path so the two axes actually meet at the origin.
              Left open on the other two sides: a closed box reads as a border
              around a picture, where an L reads as two measurements.
            */}
            <path
              className="stroke-marketing-text-secondary"
              d={`M ${String(PAD_L)} ${String(PAD_T)} V ${String(axisFloor)} H ${String(W - PAD_R)}`}
              fill="none"
              strokeWidth={1.2}
            />

            <line
              className="stroke-marketing-border"
              strokeDasharray="4 5"
              strokeWidth={1}
              x1={PAD_L}
              x2={W - PAD_R}
              y1={y(midRarity)}
              y2={y(midRarity)}
            />
            <line
              className="stroke-marketing-border"
              strokeDasharray="4 5"
              strokeWidth={1}
              x1={x(midTime)}
              x2={x(midTime)}
              y1={PAD_T}
              y2={axisFloor}
            />

            {/*
              Three numbers on the vertical, not five. The ceiling and the zero
              bound the axis, and the median is the only interior value a reader
              needs to place a face against -- a full ladder of gridlines behind
              fifty pictures is noise. The median's label is positioned from the
              same number the dashed line is drawn at, so the two cannot drift.
            */}
            {[
              { key: "top", value: yMax },
              { key: "mid", value: midRarity },
              { key: "zero", value: 0 },
            ].map((tick) => (
              <text
                className="assess-label fill-marketing-text-secondary"
                key={`y-${tick.key}`}
                textAnchor="end"
                x={PAD_L - 12}
                y={y(tick.value) + 3.5}
              >
                {tickLabel(tick.value)}
              </text>
            ))}

            {TICKS.map((fraction) => {
              const value = Math.round(xMax * fraction);
              return (
                <g key={`x-${String(fraction)}`}>
                  <line
                    className="stroke-marketing-text-secondary"
                    strokeWidth={1.2}
                    x1={x(value)}
                    x2={x(value)}
                    y1={axisFloor}
                    y2={axisFloor + 5}
                  />
                  <text
                    className="assess-label fill-marketing-text-secondary"
                    textAnchor="middle"
                    x={x(value)}
                    y={axisFloor + 22}
                  >
                    {durationLabel(value)}
                  </text>
                </g>
              );
            })}

            {/*
              Rotated up the axis it names, so neither title steals plot width.
              Sentence case rather than the `assess-label` caps of the ticks:
              these are titles for the measurements, not one more tick to read.
            */}
            <text
              className="fill-marketing-text-muted"
              fontSize={12.5}
              textAnchor="middle"
              transform={`rotate(-90 20 ${String(PAD_T + INNER_H / 2)})`}
              x={20}
              y={PAD_T + INNER_H / 2}
            >
              Rarity (rarer ↑)
            </text>
            <text
              className="fill-marketing-text-muted"
              fontSize={12.5}
              textAnchor="middle"
              x={PAD_L + INNER_W / 2}
              y={H - 10}
            >
              Time on the exam (longer →)
            </text>

            {/*
              Each leader runs the whole way to its mark's centre. The name and
              the face are both drawn over this layer, so they cover their own
              ends of the line and no gap has to be measured to keep the dashes
              off the picture.
            */}
            {named.map((point) => (
              <line
                className="stroke-marketing-text-secondary"
                key={`leader-${point.attemptId}`}
                strokeDasharray="3 3"
                strokeWidth={1}
                x1={labelX.get(point.attemptId) ?? x(point.minutes)}
                x2={x(point.minutes)}
                y1={LABEL_Y + 7}
                y2={y(point.rarity)}
              />
            ))}
          </svg>

          {points.map((point) => (
            <span
              className="absolute flex size-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
              key={point.attemptId}
              onPointerEnter={(event) => {
                setTip({
                  x: event.clientX,
                  y: event.clientY,
                  text: `${truncateText(label(point), 28)}\nrarity: ${point.rarity.toFixed(2)}\ntook: ${durationLabel(point.minutes)}\nconfirmed: ${String(point.confirmed ?? 0)} of ${String(point.submitted)}`,
                });
              }}
              onPointerLeave={() => {
                setTip(null);
              }}
              style={{
                left: `${String(leftPct(point.minutes))}%`,
                top: `${String(topPct(point.rarity))}%`,
              }}
            >
              {mark(point, isNamed.has(point.attemptId))}
            </span>
          ))}

          {named.map((point) => (
            <span
              aria-hidden
              className="assess-name text-marketing-text pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap"
              key={`name-${point.attemptId}`}
              style={{
                left: `${String(((labelX.get(point.attemptId) ?? x(point.minutes)) / W) * 100)}%`,
                top: `${String((LABEL_Y / H) * 100)}%`,
              }}
            >
              {truncateText(label(point), 22)}
            </span>
          ))}
        </div>
      </div>

      <p className="assess-sub text-marketing-text-muted border-marketing-border-light mt-4 max-w-[74ch] border-t pt-3">
        {points.length} of {entries.length} attempts on this board{" "}
        {points.length === 1 ? "carries" : "carry"} both measurements.{" "}
        {untimed > 0
          ? `${timing.subject} ${timing.singular ? "was" : "were"} submitted before a sitting had a start to measure from, so ${timing.singular ? "it carries" : "they carry"} no duration and never will. `
          : null}
        {unscored > 0
          ? `${String(unscored)} ${unscored === 1 ? "is" : "are"} still with the checkers. `
          : null}
        {ungrouped > 0
          ? `${String(ungrouped)} ${ungrouped === 1 ? "has" : "have"} been scored, but ${ungrouped === 1 ? "its findings have" : "their findings have"} not been grouped, so ${ungrouped === 1 ? "it has" : "they have"} no rarity to plot. `
          : null}
        The dashed lines are the medians of each axis. A sitting is measured
        from opening the exam room to submitting, and nothing rewards being
        quick.
      </p>

      {tip ? (
        <span
          aria-hidden
          className="bg-marketing-text text-marketing-cream-light pointer-events-none fixed z-20 rounded-[3px] px-2 py-1.5 font-mono text-[11.5px] leading-[1.5] whitespace-pre"
          style={{ left: tip.x + 14, top: tip.y + 16 }}
        >
          {tip.text}
        </span>
      ) : null}
    </div>
  );
}
